import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Instagram, 
  Facebook, 
  Star,
  ArrowLeft,
  ExternalLink,
  Heart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { trackEvent } from '../../utils/analytics';
import { useHubProperty, hubPath } from './hubConfig';
import { HubLogo } from './HubBrand';

/**
 * Social Links Page
 * 
 * Social media links + review request
 * 
 * Route: /hub/social
 */
const SocialPage = () => {
  const { slug, propertyId } = useHubProperty();
  const socialLinks = [
    {
      name: 'Instagram',
      handle: '@capitolviewstay',
      url: 'https://www.instagram.com/capitolviewstay/',
      icon: Instagram,
      color: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400',
      followers: '500+'
    },
    {
      name: 'Facebook',
      handle: 'Capitol View Stays',
      url: 'https://www.facebook.com/capitolviewstays',
      icon: Facebook,
      color: 'bg-blue-600',
      followers: ''
    }
  ];

  const handleSocialClick = (platform) => {
    trackEvent('social_click', { platform, property_id: propertyId });
  };

  const handleReviewClick = () => {
    trackEvent('review_click', { source: 'social_page', property_id: propertyId });
  };

  return (
    <div data-testid="hub-social-page" className="min-h-screen bg-slate-900 max-w-lg mx-auto">
      {/* Header */}
      <div className="px-4 pt-6 pb-4">
        <Link to={hubPath(slug)} data-testid="hub-back-link" className="inline-flex items-center text-slate-400 hover:text-white mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Hub
        </Link>
        
        <div className="text-center">
          <div className="mb-6"><HubLogo /></div>
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Follow Us</h1>
          <p className="text-slate-400 text-sm">
            Tag us for a chance to be featured!
          </p>
        </div>
      </div>

      {/* Social Links */}
      <div className="px-4 pb-6">
        <div className="space-y-3">
          {socialLinks.map(social => {
            const Icon = social.icon;
            return (
              <a
                key={social.name}
                href={social.url}
                data-testid={`social-link-${social.name.toLowerCase()}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleSocialClick(social.name)}
                className="block"
              >
                <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-700/50 transition-all hover:scale-[1.02]">
                  <CardContent className="p-0">
                    <div className="flex items-center">
                      <div className={`${social.color} w-16 h-16 flex items-center justify-center flex-shrink-0 rounded-l-lg`}>
                        <Icon className="h-7 w-7 text-white" />
                      </div>
                      <div className="flex-1 px-4 py-3">
                        <h3 className="text-white font-semibold">{social.name}</h3>
                        <p className="text-slate-400 text-sm">{social.handle}</p>
                      </div>
                      <ExternalLink className="h-5 w-5 text-slate-500 mr-4" />
                    </div>
                  </CardContent>
                </Card>
              </a>
            );
          })}
        </div>
      </div>

      {/* Feature Request */}
      <div className="px-4 pb-6">
        <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30">
          <CardContent className="p-6 text-center">
            <p className="text-amber-200 text-lg mb-2">📸</p>
            <h3 className="text-white font-semibold mb-2">Share Your Stay</h3>
            <p className="text-slate-300 text-sm mb-4">
              Post a photo of your stay and tag <span className="text-amber-400">@capitolviewstay</span> for a chance to be featured on our page!
            </p>
            <p className="text-slate-400 text-xs">
              Use #CapitolViewStays #WashingtonDC
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Review Section */}
      <div className="px-4 pb-8">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6 text-center">
            <div className="flex justify-center gap-1 mb-3">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="h-6 w-6 text-amber-400 fill-amber-400" />
              ))}
            </div>
            <h3 className="text-white font-semibold mb-2">Enjoying Your Stay?</h3>
            <p className="text-slate-400 text-sm mb-4">
              We'd love to hear from you! Your review helps other travelers discover us.
            </p>
            <a
              href="https://g.page/r/CVhI4ZNEkpyiEAI/review"
              data-testid="google-review-link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleReviewClick}
            >
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                <Star className="h-4 w-4 mr-2" />
                Leave a Review
              </Button>
            </a>
          </CardContent>
        </Card>
      </div>

      {/* TikTok Placeholder */}
      <div className="px-4 pb-8 text-center">
        <p className="text-slate-500 text-xs">
          TikTok coming soon! 🎬
        </p>
      </div>
    </div>
  );
};

export default SocialPage;
